import { RadioGroup } from "@headlessui/react";
import _, { get, has } from "lodash";
import { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { Button } from "@shared/ui";
import Policy from "../components/Policy";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import CarrierMark from "../components/CarrierMark";
import {
  isAwaiting,
  isStarted,
  linkDocuments,
  linkPolicy,
  processDirectLink,
  validatePolicy,
} from "../lib/axle";
import { MAX_HITL_STARTED_RETRIES } from "../lib/constants";
import { getNextStepBeforeSuccess, isIntegrationV123 } from "../lib/utility";
import CarrierLoader from "./CarrierLoader";
import EmptySearchMark from "../components/EmptySearchMark";

const Confirm = ({
  nextStep,
  step,
  accountInfo,
  loginInformation,
  session,
  setPolicyInfo,
  setLoginInformation,
  carriers,
  setLinkError,
  posthog,
  setPartnerCarrierConfig,
  setShowNav,
  isUserAuthenticated,
  integration,
}) => {
  useEffect(() => {
    posthog.capture("$pageview", {
      step,
      carrier: get(loginInformation, "carrier.id"),
    });
  }, [posthog]);

  const ignitionToken = session.id;
  const policies = get(accountInfo, "policies", []);
  const carrierName = get(loginInformation, "carrier.name");

  const [error, setError] = useState(null);
  const [policyIndex, setPolicyIndex] = useState(0);
  const [policy, setPolicy] = useState(get(policies, policyIndex));

  // Configure carrier loader
  const { promiseInProgress } = usePromiseTracker({ area: "carrier-loader" });
  const baseLoadingSteps = [
    "Establishing a secure connection",
    "Contacting your carrier",
    "Retrieving policy details",
  ];

  // Add policy actions step if configured in scopes
  const policyActions = get(session, "config.scopes.policyActions", []);
  const loadingSteps =
    policyActions && policyActions.length > 0
      ? [...baseLoadingSteps, "Updating policy lienholder"]
      : baseLoadingSteps;

  const onClick = async (index) => {
    setPolicyIndex(index);
  };

  const checkIfBackupDocumentRequired = async (ignitionToken, policyId) => {
    // Call validate policy with backup-document-required type
    console.log(policyId);
    const [validatePolicyResponse, validatePolicyError] = await validatePolicy(
      ignitionToken,
      policyId,
      "backup-document-required"
    );
    if (validatePolicyError) {
      console.error(JSON.stringify(validatePolicyError));
    }

    // Extract boolean from results
    const backupDocumentRequired = get(
      validatePolicyResponse,
      "backupDocumentRequired"
    );

    return backupDocumentRequired;
  };

  const fetchPolicyAndDocuments = async (
    policyType,
    policyNumber,
    policyLabel,
    policyHolder,
    startedPollAttempts = 0
  ) => {
    // Hide nav buttons while CarrierLoader is rendered for accessibility
    setShowNav(false);

    const v123 = isIntegrationV123(loginInformation, integration, session);
    let linkPolicyResponse, linkPolicyError;
    // The backend names how it minted the policy; user-portal when it sends none.
    let processDetail = "user-portal";

    if (v123) {
      const [directLinkResponse, directLinkError] = await processDirectLink(
        ignitionToken,
        integration,
        "getPolicy",
        {
          policyNumber,
          policyType,
          ...(policyLabel ? { policyLabel } : {}),
          ...(policyHolder ? { policyHolder } : {}),
        }
      );

      if (directLinkError) {
        linkPolicyError = directLinkError;
      } else if (isAwaiting(directLinkResponse)) {
        setError(
          "Still finalizing your policy selection. Please try again in a moment."
        );
        return;
      } else if (isStarted(directLinkResponse)) {
        // hitlAi's poll budget elapsed before the extraction finished. Re-issue the
        // same operation to keep polling: the selection carries a deterministic
        // nonce, so a second dispatch dedups server side and only the poll repeats.
        if (startedPollAttempts < MAX_HITL_STARTED_RETRIES) {
          return fetchPolicyAndDocuments(
            policyType,
            policyNumber,
            policyLabel,
            policyHolder,
            startedPollAttempts + 1
          );
        }
        setLinkError("retrieve your policy details in time");
        nextStep("carrier-error");
        return;
      } else {
        // The backend mints only on a passed verdict, so anything else has no
        // FuelTank policy behind it.
        const policyValidation = get(directLinkResponse, "policyValidation");
        if (get(policyValidation, "status") !== "passed") {
          posthog.capture("v123-policy-validation-failed", {
            step,
            carrier: get(loginInformation, "carrier.id"),
            issueCount: get(policyValidation, "issueCount", null),
          });
          setLinkError("verify your policy details");
          nextStep("carrier-error");
          return;
        }

        linkPolicyResponse = get(directLinkResponse, "constructedPolicies");
        processDetail = get(directLinkResponse, "processDetail", processDetail);
        // A passed verdict always carries the minted id. Without one there is
        // nothing to hand Success, and the carrier's own policy number would
        // request an authCode for a policy that does not exist.
        if (!get(linkPolicyResponse, "id")) {
          setLinkError("retrieve policy details");
          nextStep("carrier-error");
          return;
        }
      }
    } else {
      [linkPolicyResponse, linkPolicyError] = await linkPolicy(
        ignitionToken,
        policyType,
        policyNumber
      );
    }

    if (linkPolicyError) {
      switch (get(linkPolicyError, "code")) {
        case 400:
          if (
            linkPolicyError.message ===
            "Policy number is invalid or does not exist."
          ) {
            setLinkError("retrieve policy details");
            nextStep("carrier-error");
            return;
          } else if (
            linkPolicyError.message ===
            "Sorry, your policy is being processed or is missing data. Please ensure policy details are available in your account, and if not, contact your carrier."
          ) {
            nextStep("policy-pending");
            return;
          } else if (
            linkPolicyError.message ===
            "Sorry, your insurance account or policy has not been set up for online access."
          ) {
            nextStep("account-pending");
            return;
          } else if (linkPolicyError.message === "Policy is not active.") {
            nextStep("policy-not-active");
            return;
          }
          return;
        case 401:
        case 403:
          setLinkError(linkPolicyError.message);
          nextStep("session-expired");
          return;
        default:
          setLinkError("retrieve policy details");
          nextStep("carrier-error");
          return;
      }
    }

    setPolicyInfo({ id: get(linkPolicyResponse, "id") });

    // Only now, once the V123 branch above is done with it: isIntegrationV123 reads
    // result === "link" as "legacy run", so stamping any earlier would send this
    // confirmation down linkPolicy and never complete the browser agent's
    // policy-wait. Success needs it for the ignition.completed event.
    if (v123) {
      setLoginInformation({
        ...loginInformation,
        result: "link",
        resultDetail: processDetail,
      });
    }

    if (!v123) {
      const [, documentsError] = await linkDocuments(
        ignitionToken,
        policyType,
        policyNumber,
        get(linkPolicyResponse, "id")
      );
      // [Legacy comment] Attempt to link documents to policy, but do not throw on error
      // More details on implementation: https://github.com/Axle-Labs/ignition/blob/main/src/steps/Confirm.js#L76C7-L76C72
      //NOTE: Whenever linkDocuments threw an error, we never did anything besides catching it
      if (documentsError) {
        console.log("Unable to linkDocuments...");
      }
    }

    // If backup document enabled, validate policy to check if backup document is required and go to manual document upload
    if (get(session, "config.backup-document.enabled")) {
      const backupDocumentRequired = await checkIfBackupDocumentRequired(
        ignitionToken,
        get(linkPolicyResponse, "id")
      );

      if (backupDocumentRequired === true) {
        nextStep("backup-document-required");
        return;
      }
    }

    // If require backup docs, go to manual_policy_docs, else go to success
    if (get(session, "config.documentation.enabled")) {
      nextStep("manual-policy-document");
    } else {
      // Skip to validatePolicy OR success if user is a already log
      isUserAuthenticated
        ? nextStep(getNextStepBeforeSuccess(session))
        : nextStep("axle-account-sign-up");
    }
  };

  const onSubmit = async () => {
    posthog.capture("confirm-policy-selection", {
      step,
      carrier: get(loginInformation, "carrier.id"),
    });

    const {
      policyType,
      policyNumber,
      policyPartner,
      policyLabel,
      policyHolder,
    } = get(policies, policyIndex);

    // If policy has a partner, redirect to partner login page
    if (policyPartner) {
      let policyPartnerCarrierConfig = carriers.find(
        (carrier) => carrier.id === policyPartner
      );
      setPartnerCarrierConfig({ carrier: policyPartnerCarrierConfig });
      nextStep("redirect-login");
      return;
    }

    await trackPromise(
      fetchPolicyAndDocuments(
        policyType,
        policyNumber,
        policyLabel,
        policyHolder
      ),
      "carrier-loader"
    );

    // Show nav buttons after CarrierLoader is unrendered
    setShowNav(true);
  };

  return (
    <>
      {promiseInProgress ? (
        <CarrierLoader
          loginInformation={loginInformation}
          loadingHeader="Linking policy"
          loadingSteps={loadingSteps}
        />
      ) : _.size(policies) === 0 ? (
        <>
          <StepBody centered className="items-center gap-4 text-center">
            <EmptySearchMark className="mb-4" />
            <StepHeading
              align="center"
              title="No policies found"
              description={`We didn’t find any compatible policies on your ${
                carrierName ? `${carrierName} ` : ""
              }account.`}
            />
          </StepBody>
        </>
      ) : (
        <>
          <StepBody>
            <CarrierMark loginInformation={loginInformation} />
            <StepHeading
              title="Choose a policy"
              description="Axle will only share information from the selected policy."
            />
            <div role="group" className="flex flex-col gap-3" aria-label="Available policies">
              {error && (
                <div
                  className="rounded border border-danger-border bg-fail-bg p-3 text-body-lg text-danger-ink"
                  role="status"
                >
                  {error}
                </div>
              )}
              <RadioGroup
                value={policy}
                onChange={setPolicy}
                className="flex flex-col gap-3"
              >
                {policies.map((policy, index) => (
                  <Policy
                    key={
                      get(policy, "policyNumber") ??
                      `${get(policy, "policyType")}-${get(policy, "policyLabel")}-${index}`
                    }
                    policy={policy}
                    onClick={() => onClick(index)}
                  />
                ))}
              </RadioGroup>
            </div>
          </StepBody>
          <StepFooter>
            <Button
              type={"submit"}
              onClick={() => onSubmit()}
            >
              Continue
            </Button>
          </StepFooter>
        </>
      )}
    </>
  );
};

export default Confirm;
